/// useToolbarActions: composed hook for Deploy/Run/Stop toolbar actions.
/// Provides action functions and derived button states using WorkerManager,
/// editorStore, and engineStore. All feedback goes to the Console panel.
///
/// Phase 25, Plan 01.

import { useState, useMemo, useCallback } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import { useEditorStore } from "@/stores/editorStore";
import { useEngineStore } from "@/stores/engineStore";

// ---------------------------------------------------------------------------
// Console logging helper
// ---------------------------------------------------------------------------

/** Push a structured event to the engine event log (Console panel). */
function logToConsole(eventType: string, message: string): void {
  useEngineStore.getState().pushEvent({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: Date.now(),
    workerId: "toolbar",
    eventType,
    payload: { message },
  });
}

// ---------------------------------------------------------------------------
// Export helper -- one-shot Promise waiting for documentExported event
// ---------------------------------------------------------------------------

async function getExportedContent(
  editorWorkerId: string,
): Promise<{ content: string; format: string }> {
  const manager = getWorkerManager();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsub();
      reject(new Error("Export timed out"));
    }, 10000);

    const unsub = manager.onEditorEvent("documentExported", (sourceId, env) => {
      if (sourceId !== editorWorkerId) return;
      clearTimeout(timeout);
      unsub();
      resolve(env.payload as { content: string; format: string });
    });

    const host = manager.getHost();
    const worker = host.getWorker(editorWorkerId);
    if (!worker) {
      clearTimeout(timeout);
      unsub();
      reject(new Error("Editor worker not found"));
      return;
    }
    worker.postMessage({ type: "exportForDeploy" });
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface ToolbarActions {
  deploy: () => Promise<void>;
  run: () => Promise<void>;
  stop: () => Promise<void>;
  canDeploy: boolean;
  canRun: boolean;
  canStop: boolean;
  isDeploying: boolean;
  isStopping: boolean;
}

/**
 * Compose editor tab state, engine worker commands, and store-derived button
 * states into a clean action API for the Toolbar.
 */
export function useToolbarActions(): ToolbarActions {
  const [isDeploying, setIsDeploying] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Selectors -- minimal re-render surface via individual selectors
  const activeTab = useEditorStore((s) => {
    const id = s.activeTabId;
    return id ? s.tabs.find((t) => t.id === id) : undefined;
  });

  const deployedProcesses = useEngineStore((s) => s.deployedProcesses);
  const deployedNodeFlows = useEngineStore((s) => s.deployedNodeFlows);
  const runningInstances = useEngineStore((s) => s.runningInstances);

  // Derived booleans
  const hasActiveTab = !!activeTab;
  const hasDeployment = deployedProcesses.length > 0 || deployedNodeFlows.length > 0;
  const isRunning = runningInstances.some(
    (i) => i.state === "running" || i.state === "created",
  );

  const canDeploy = hasActiveTab && !isDeploying;
  const canRun = hasDeployment && !isRunning && !isDeploying;
  const canStop = isRunning && !isStopping;

  // -------------------------------------------------------------------------
  // deploy()
  // -------------------------------------------------------------------------

  const deploy = useCallback(async () => {
    const tab = useEditorStore.getState().getActiveTab();
    if (!tab) return;

    setIsDeploying(true);
    try {
      const editorType = tab.editorType;
      const manager = getWorkerManager();

      // Find editor worker for this tab
      const editorState = useEditorStore.getState().editorStates.get(tab.id);
      let editorWorkerId = editorState?.workerId;
      if (!editorWorkerId) {
        editorWorkerId = manager.getWorkerIdByRole(
          editorType === "bpmn" ? "bpmn-editor" : "node-editor",
        );
      }
      if (!editorWorkerId) {
        logToConsole("deployError", "No editor worker available for export");
        return;
      }

      // Get serialized document content via exportForDeploy
      const { content } = await getExportedContent(editorWorkerId);
      if (!content) {
        logToConsole("deployError", "Export returned empty content");
        return;
      }

      // Find engine worker
      const engineWorkerId = manager.getWorkerIdByRole(
        editorType === "bpmn" ? "bpmn-engine" : "node-engine",
      );
      if (!engineWorkerId) {
        logToConsole("deployError", `No ${editorType} engine worker available`);
        return;
      }

      // Send deploy command to engine
      const deployPayload =
        editorType === "bpmn"
          ? { bpmn_xml: content, resource_name: tab.title }
          : { nodeflow_json: content, resource_name: tab.title };

      const response = await manager.sendCommand(
        engineWorkerId,
        {
          type: "deploy",
          direction: "cmd" as const,
          payload: deployPayload,
        } as never,
        10000,
      );

      if (
        response.payload?.status === "ok" ||
        response.payload?.status === "success" ||
        response.payload?.process_key ||
        response.payload?.flow_key
      ) {
        // Register deployment in engineStore so Run button becomes enabled
        if (response.payload?.process_key) {
          useEngineStore.getState().addDeployedProcess({
            processId: String(response.payload.process_key),
            resourceName: tab.title,
            version: 1,
            deployedAt: Date.now(),
          });
        } else if (response.payload?.flow_key) {
          useEngineStore.getState().addDeployedNodeFlow({
            flowId: String(response.payload.flow_key),
            resourceName: tab.title,
            version: 1,
            deployedAt: Date.now(),
          });
        }

        const label = editorType === "bpmn" ? "BPMN process" : "Node flow";
        logToConsole("deploySuccess", `Deployed ${label} "${tab.title}"`);
      } else {
        logToConsole(
          "deployError",
          `Deploy failed: ${String(response.payload?.error ?? "unknown error")}`,
        );
      }
    } catch (err) {
      logToConsole("deployError", `Deploy failed: ${String(err)}`);
    } finally {
      setIsDeploying(false);
    }
  }, []);

  // -------------------------------------------------------------------------
  // run()
  // -------------------------------------------------------------------------

  const run = useCallback(async () => {
    const manager = getWorkerManager();
    const tab = useEditorStore.getState().getActiveTab();
    const store = useEngineStore.getState();

    // Determine which deployment to run
    let engineRole: "bpmn-engine" | "node-engine" | undefined;
    let commandPayload: Record<string, unknown> | undefined;
    let commandType: string | undefined;

    const editorType = tab?.editorType;

    if (editorType === "bpmn" && store.deployedProcesses.length > 0) {
      const lastProcess = store.deployedProcesses[store.deployedProcesses.length - 1];
      engineRole = "bpmn-engine";
      commandType = "start";
      commandPayload = { process_key: lastProcess.processId };
    } else if (editorType === "nodeflow" && store.deployedNodeFlows.length > 0) {
      const lastFlow = store.deployedNodeFlows[store.deployedNodeFlows.length - 1];
      engineRole = "node-engine";
      commandType = "execute";
      commandPayload = { flow_key: lastFlow.flowId };
    } else if (store.deployedProcesses.length > 0) {
      // Fallback: try BPMN deployments
      const lastProcess = store.deployedProcesses[store.deployedProcesses.length - 1];
      engineRole = "bpmn-engine";
      commandType = "start";
      commandPayload = { process_key: lastProcess.processId };
    } else if (store.deployedNodeFlows.length > 0) {
      // Fallback: try Node deployments
      const lastFlow = store.deployedNodeFlows[store.deployedNodeFlows.length - 1];
      engineRole = "node-engine";
      commandType = "execute";
      commandPayload = { flow_key: lastFlow.flowId };
    }

    if (!engineRole || !commandType || !commandPayload) {
      logToConsole("runError", "No deployment available to run");
      return;
    }

    const engineWorkerId = manager.getWorkerIdByRole(engineRole);
    if (!engineWorkerId) {
      logToConsole("runError", `No ${engineRole} worker available`);
      return;
    }

    try {
      const response = await manager.sendCommand(
        engineWorkerId,
        {
          type: commandType,
          direction: "cmd" as const,
          payload: commandPayload,
        } as never,
        15000,
      );

      if (
        response.payload?.status === "ok" ||
        response.payload?.status === "success" ||
        response.payload?.instance_key
      ) {
        const instanceKey = String(response.payload?.instance_key ?? "unknown");
        logToConsole("runSuccess", `Started instance ${instanceKey}`);
      } else {
        logToConsole(
          "runError",
          `Run failed: ${String(response.payload?.error ?? "unknown error")}`,
        );
      }
    } catch (err) {
      logToConsole("runError", `Run failed: ${String(err)}`);
    }
  }, []);

  // -------------------------------------------------------------------------
  // stop()
  // -------------------------------------------------------------------------

  const stop = useCallback(async () => {
    const store = useEngineStore.getState();
    const manager = getWorkerManager();
    setIsStopping(true);

    const activeInstances = store.runningInstances.filter(
      (i) => i.state === "running" || i.state === "created",
    );

    for (const instance of activeInstances) {
      // Determine which engine worker to command
      const deployedBPMN = store.deployedProcesses.find(
        (p) => p.processId === instance.processId,
      );
      const engineRole = deployedBPMN ? "bpmn-engine" : "node-engine";
      const engineWorkerId = manager.getWorkerIdByRole(engineRole);

      if (!engineWorkerId) {
        // Fallback: just update local state
        store.updateInstanceState(instance.instanceKey, "suspended");
        logToConsole("stopAction", "Engine stopped (no worker available)");
        continue;
      }

      try {
        await manager.sendCommand(
          engineWorkerId,
          {
            type: "cancelInstance",
            direction: "cmd" as const,
            payload: { instance_key: instance.instanceKey },
          } as never,
          3000,
        );
        store.updateInstanceState(instance.instanceKey, "suspended");
        logToConsole("stopAction", "Engine stopped");
      } catch {
        // Force-kill: timeout expired
        store.updateInstanceState(instance.instanceKey, "suspended");
        logToConsole("stopAction", "Engine force-terminated (timeout)");
      }
    }

    setIsStopping(false);
  }, []);

  // Memoize the return object to avoid unnecessary re-renders in Toolbar
  return useMemo(
    () => ({
      deploy,
      run,
      stop,
      canDeploy,
      canRun,
      canStop,
      isDeploying,
      isStopping,
    }),
    [deploy, run, stop, canDeploy, canRun, canStop, isDeploying, isStopping],
  );
}
